import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, LabelList,
} from "recharts";
import { getAnalyticsSummary, getScanGraph, listQRCodes, adminListUsers } from "../services/api";

const COLORS = [
  "#0046FF", "#00B8D9", "#1E9E5A", "#F59E0B", "#8B5CF6",
  "#EC4899", "#F97316", "#14B8A6", "#6366F1", "#84CC16",
];

const PERIODS = [
  { value: "daily", label: "Today", phrase: "today" },
  { value: "weekly", label: "This Week", phrase: "this week" },
  { value: "monthly", label: "This Month", phrase: "this month" },
  { value: "custom", label: "Custom", phrase: "in the selected range" },
];

const Svg = ({ size = 16, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const QrIcon = ({ size }) => (
  <Svg size={size}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM20 14v.01M14 20h3M20 17v4" />
  </Svg>
);

const ScanIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" />
    <path d="M4 12h16" />
  </Svg>
);

const TrendIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M3 17l6-6 4 4 8-8" />
    <path d="M14 7h7v7" />
  </Svg>
);

const ChartIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </Svg>
);

function BarTooltip({ active, payload, totalScans }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const share = totalScans ? Math.round((row.scans / totalScans) * 100) : 0;

  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{row.name}</div>
      <div className="chart-tooltip-row">
        <span className="chart-tooltip-dot" style={{ background: row.color }} />
        <span className="chart-tooltip-name">Scans</span>
        <span className="chart-tooltip-value">{row.scans}</span>
      </div>
      <div className="chart-tooltip-row">
        <span className="chart-tooltip-dot" style={{ background: "#9CA3AF" }} />
        <span className="chart-tooltip-name">Share of total</span>
        <span className="chart-tooltip-value">{share}%</span>
      </div>
    </div>
  );
}

const CalendarIcon = ({ size }) => (
  <Svg size={size}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 10h18" />
  </Svg>
);

const ArrowDownIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M12 5v14M6 13l6 6 6-6" />
  </Svg>
);

const ArrowUpIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </Svg>
);

const fmtDate = (d) =>
  d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const fromInput = (value) => new Date(`${value}T00:00:00`);

function getRangeLabel(period, applied) {
  const now = new Date();
  if (period === "daily") return fmtDate(now);

  if (period === "weekly") {
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return `${fmtDate(monday)} – ${fmtDate(sunday)}`;
  }

  if (period === "monthly") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return `${fmtDate(first)} – ${fmtDate(last)}`;
  }

  if (applied && applied.start && applied.end) {
    return applied.start === applied.end
      ? fmtDate(fromInput(applied.start))
      : `${fmtDate(fromInput(applied.start))} – ${fmtDate(fromInput(applied.end))}`;
  }
  return null;
}

const ChevronIcon = ({ size }) => (
  <Svg size={size}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);

const XIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Svg>
);

const SearchIcon = ({ size }) => (
  <Svg size={size}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
);

const CheckIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M5 12.5 10 17.5 19 7.5" />
  </Svg>
);

/* Generic multi-select dropdown with search and removable chips.
   Works for any option list — pass `labelKey` to say which field to show
   (e.g. "title" for QR codes, "username" for users). */
function MultiSelect({
  options,
  value,
  onChange,
  labelKey = "title",
  placeholder = "All items",
  searchPlaceholder = "Search",
  emptyText = "No results found",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
    if (!open) setSearch("");
  }, [open]);

  const getLabel = (o) => o[labelKey] ?? o.title ?? o.username ?? "";

  const selected = options.filter((o) => value.includes(o.id));
  const filtered = options.filter((o) =>
    getLabel(o).toLowerCase().includes(search.trim().toLowerCase())
  );

  const toggle = (id) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const remove = (e, id) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== id));
  };

  const MAX_CHIPS = 2;

  return (
    <div className="multi-select" ref={rootRef}>
      <div
        className={`ms-control${open ? " open" : ""}`}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        <div className="ms-values">
          {selected.length === 0 && <span className="ms-placeholder">{placeholder}</span>}
          {selected.slice(0, MAX_CHIPS).map((o) => (
            <span key={o.id} className="ms-chip">
              <span className="ms-chip-label">{getLabel(o)}</span>
              <button
                type="button"
                className="ms-chip-x"
                aria-label={`Remove ${getLabel(o)}`}
                onClick={(e) => remove(e, o.id)}
              >
                <XIcon size={12} />
              </button>
            </span>
          ))}
          {selected.length > MAX_CHIPS && (
            <span className="ms-chip ms-chip-more">+{selected.length - MAX_CHIPS}</span>
          )}
        </div>

        {selected.length > 0 && (
          <button
            type="button"
            className="ms-clear"
            aria-label="Clear all"
            title="Clear all"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
          >
            <XIcon size={14} />
          </button>
        )}
        <span className="ms-chevron"><ChevronIcon size={16} /></span>
      </div>

      {open && (
        <div className="ms-menu">
          <div className="ms-search">
            <SearchIcon size={15} />
            <input
              ref={searchRef}
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button type="button" aria-label="Clear search" onClick={() => setSearch("")}>
                <XIcon size={13} />
              </button>
            )}
          </div>

          <div className="ms-actions">
            <button
              type="button"
              onClick={() => onChange([...new Set([...value, ...filtered.map((o) => o.id)])])}
              disabled={filtered.length === 0}
            >
              Select {search ? "results" : "all"}
            </button>
            <button type="button" onClick={() => onChange([])} disabled={value.length === 0}>
              Clear
            </button>
          </div>

          <div className="ms-list" role="listbox" aria-multiselectable="true">
            {filtered.length === 0 ? (
              <div className="ms-empty">{emptyText}</div>
            ) : (
              filtered.map((o) => {
                const checked = value.includes(o.id);
                return (
                  <div
                    key={o.id}
                    role="option"
                    aria-selected={checked}
                    className={`ms-option${checked ? " selected" : ""}`}
                    onClick={() => toggle(o.id)}
                  >
                    <span className="ms-check">{checked && <CheckIcon size={12} />}</span>
                    <span className="ms-option-label">{getLabel(o)}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const truncate = (text, max = 18) =>
  text && text.length > max ? `${text.slice(0, max - 1)}…` : text;

export default function AdminAnalytics() {
  const [users, setUsers] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [summary, setSummary] = useState({ total_qr_codes: 0, total_scans: 0 });
  const [codes, setCodes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [period, setPeriod] = useState("daily");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [graphData, setGraphData] = useState([]);
  const [series, setSeries] = useState({});
  const [loading, setLoading] = useState(true);
  const [showZero, setShowZero] = useState(false);
  const [appliedRange, setAppliedRange] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    adminListUsers().then(({ data }) => setUsers(data.results || data));
  }, []);

  // Serialized so effects don't refire just because a new array reference was created
  const userIdsKey = userIds.join(",");

  useEffect(() => {
    const scopeParams = userIds.length ? { user: userIds } : {};
    getAnalyticsSummary(scopeParams).then(({ data }) => setSummary(data));
    listQRCodes(scopeParams).then(({ data }) => setCodes(data.results || data));
    setSelectedIds([]); // QR selection doesn't carry over when the user scope changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdsKey]);

  const loadGraph = () => {
    if (period === "custom" && (!start || !end)) {
      // Nothing to search yet: wait for the user to pick both dates and press Apply.
      setGraphData([]);
      setSeries({});
      setAppliedRange(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = { period };
    if (userIds.length) params.user = userIds;
    if (period === "custom") {
      params.start = start;
      params.end = end;
      setAppliedRange({ start, end });
    }
    getScanGraph(params)
      .then(({ data }) => {
        setGraphData(data.data || []);
        setSeries(data.series || {});
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadGraph, [period, userIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* One bar per QR code: sum each code's scans across the whole period,
     then sort highest first. */
  const { barData, periodTotal } = useMemo(() => {
    const selectedNames = new Set(
      codes.filter((qr) => selectedIds.includes(qr.id)).map((qr) => qr.title)
    );
    const hasSelection = selectedIds.length > 0;

    const rows = Object.keys(series)
      .map((key, i) => ({ key, i }))
      .filter(({ key }) => !hasSelection || selectedNames.has(series[key]))
      .map(({ key, i }) => ({
        key,
        color: COLORS[i % COLORS.length],
        name: series[key],
        scans: graphData.reduce((sum, row) => sum + (Number(row[key]) || 0), 0),
      }));

    if (showZero) {
      const known = new Set(rows.map((r) => r.name));
      const candidates = hasSelection ? codes.filter((qr) => selectedIds.includes(qr.id)) : codes;
      candidates.forEach((qr) => {
        if (!known.has(qr.title)) {
          rows.push({
            key: `qr-${qr.id}`,
            color: COLORS[rows.length % COLORS.length],
            name: qr.title,
            scans: 0,
          });
        }
      });
    }

    const total = rows.reduce((sum, r) => sum + r.scans, 0);
    const visible = rows.filter((r) => showZero || r.scans > 0).sort((a, b) => (sortOrder === "desc" ? b.scans - a.scans : a.scans - b.scans));
    return { barData: visible, periodTotal: total };
  }, [graphData, series, codes, showZero, selectedIds, sortOrder]);

  const avgScans = summary.total_qr_codes
    ? (summary.total_scans / summary.total_qr_codes).toFixed(1)
    : "0";

  const rangeLabel = getRangeLabel(period, appliedRange);
  const periodPhrase = PERIODS.find((p) => p.value === period)?.phrase || "";
  const chartMinWidth = Math.max(480, barData.length * 88);

  const selectedUsers = users.filter((u) => userIds.includes(u.id));
  const scopeSuffix =
    selectedUsers.length === 1
      ? `${selectedUsers[0].username}'s QR codes`
      : selectedUsers.length > 1
      ? `${selectedUsers.length} selected users' QR codes`
      : null;
  const statFootText = scopeSuffix ? "For selected user(s)" : "Across all users";

  return (
    <div>
      <div className="page-title">Admin Analytics</div>
      <div className="page-subtitle">
        {scopeSuffix ? `Tracking ${scopeSuffix}` : "Track how QR codes are performing across all users"}
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <div className="card stat-card stat-blue">
          <div className="stat-head"><QrIcon /> Total QR Codes</div>
          <div className="stat-value">{summary.total_qr_codes}</div>
          <div className="stat-foot">{statFootText}</div>
          <div className="stat-watermark"><QrIcon size={96} /></div>
        </div>

        <div className="card stat-card stat-green">
          <div className="stat-head"><ScanIcon /> Total Scans</div>
          <div className="stat-value">{summary.total_scans}</div>
          <div className="stat-foot">All time</div>
          <div className="stat-watermark"><ScanIcon size={96} /></div>
        </div>

        <div className="card stat-card stat-purple">
          <div className="stat-head"><TrendIcon /> Avg. Scans per QR</div>
          <div className="stat-value">{avgScans}</div>
          <div className="stat-foot">Total scans ÷ QR codes</div>
          <div className="stat-watermark"><TrendIcon size={96} /></div>
        </div>
      </div>

      {/* CHART CARD */}
      <div className="card chart-card">
        <div className="chart-header">
          <div>
            <div className="card-title" style={{ marginBottom: 4 }}>
              <span className="card-title-icon"><ChartIcon size={18} /></span>
              Scans by QR code {periodPhrase}
            </div>
            <div className="chart-subtitle">
              Each bar is one QR code. {showZero ? "Including" : "QR codes with"} zero scans
              {showZero ? " are shown." : " are hidden by default."}
            </div>
          </div>

          <label className="toggle-check">
            <input
              type="checkbox"
              checked={showZero}
              onChange={(e) => setShowZero(e.target.checked)}
            />
            Show zero-scan codes
          </label>
        </div>

        {/* TOOLBAR */}
        <div className="toolbar">
          <div className="toolbar-field toolbar-field-wide">
            <label className="form-label">Users</label>
            <MultiSelect
              options={users}
              value={userIds}
              onChange={setUserIds}
              labelKey="username"
              placeholder="All Users"
              searchPlaceholder="Search users"
              emptyText="No users found"
            />
          </div>

          <div className="toolbar-field toolbar-field-wide">
            <label className="form-label">QR Codes</label>
            <MultiSelect
              options={codes}
              value={selectedIds}
              onChange={setSelectedIds}
              labelKey="title"
              placeholder="All QR Codes"
              searchPlaceholder="Search QR codes"
              emptyText="No QR codes found"
            />
          </div>

          <div className="toolbar-field">
            <label className="form-label">Date Filter</label>
            <div className="segmented">
              {PERIODS.map((p) => (
                <button
                  type="button"
                  key={p.value}
                  className={period === p.value ? "active" : ""}
                  onClick={() => setPeriod(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {period === "custom" && (
            <div className="toolbar-range">
              <div className="toolbar-field">
                <label className="form-label">Start</label>
                <input
                  type="date"
                  className="form-input"
                  value={start}
                  max={end || undefined}
                  onChange={(e) => setStart(e.target.value)}
                />
              </div>
              <span className="toolbar-range-sep" aria-hidden="true">→</span>
              <div className="toolbar-field">
                <label className="form-label">End</label>
                <input
                  type="date"
                  className="form-input"
                  value={end}
                  min={start || undefined}
                  onChange={(e) => setEnd(e.target.value)}
                />
              </div>
              <button
                className="btn btn-primary toolbar-apply"
                onClick={loadGraph}
                disabled={!start || !end}
              >
                Apply
              </button>
            </div>
          )}
        </div>

        {/* PERIOD + SORT */}
        <div className="chart-meta">
          <div className="period-pill">
            <CalendarIcon size={14} />
            <span className="period-pill-label">Showing</span>
            <strong>{rangeLabel || "Pick a start and end date"}</strong>
          </div>

          <div className="segmented sort-toggle" role="group" aria-label="Sort order">
            <button
              type="button"
              className={sortOrder === "desc" ? "active" : ""}
              onClick={() => setSortOrder("desc")}
              aria-pressed={sortOrder === "desc"}
            >
              <ArrowDownIcon size={14} /> High to Low
            </button>
            <button
              type="button"
              className={sortOrder === "asc" ? "active" : ""}
              onClick={() => setSortOrder("asc")}
              aria-pressed={sortOrder === "asc"}
            >
              <ArrowUpIcon size={14} /> Low to High
            </button>
          </div>
        </div>

        {/* CHART */}
        {loading ? (
          <div className="chart-state">
            <div className="spinner" />
            Loading chart...
          </div>
        ) : barData.length === 0 ? (
          <div className="chart-state">
            <span className="chart-state-icon"><ChartIcon size={26} /></span>
            <strong>No scan data yet</strong>
            <span>
              {period === "custom" && !appliedRange
                ? "Pick a start and end date, then press Apply."
                : "Nothing was scanned in this period. Try a wider date range."}
            </span>
          </div>
        ) : (
          <div className="chart-scroll">
            <div style={{ minWidth: chartMinWidth }}>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart
                  data={barData}
                  margin={{ top: 24, right: 16, left: -8, bottom: 0 }}
                  barCategoryGap="22%"
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 5" stroke="#DDE2EC" />

                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 13, fill: "#374151" }}
                    tickFormatter={(v) => truncate(v, 14)}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    dy={8}
                  />
                  <YAxis
                    type="number"
                    allowDecimals={false}
                    domain={[0, (max) => Math.max(5, Math.ceil(max * 1.1))]}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    content={<BarTooltip totalScans={periodTotal} />}
                    cursor={{ fill: "rgba(0,70,255,0.05)" }}
                  />

                  <Bar
                    dataKey="scans"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={56}
                    minPointSize={4}
                  >
                    {barData.map((row) => (
                      <Cell key={row.key} fill={row.color} />
                    ))}
                    <LabelList
                      dataKey="scans"
                      position="top"
                      style={{ fontSize: 12, fontWeight: 600, fill: "#111827" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}